import { useState } from "react";
import { Alert, IconButton } from "@sortera/react-components";
import { CheckIcon, ClipboardListIcon } from "@sortera/react-icons";
import type { ToolStep, TurnUsage } from "../../types/index.ts";
import { Markdown } from "../../ui/markdown/index.ts";
import { Spinner } from "../../ui/spinner/index.ts";
import { ActivitySteps } from "../activity-steps/index.ts";
import { useSmoothText } from "./use-smooth-text.ts";
import { formatSek, splitErrors, stopNote } from "./utils.ts";
import styles from "./message.module.css";

export function UserMessage({ text }: { text: string }) {
  return (
    <div className={styles.userRow}>
      <div className={styles.user}>{text}</div>
    </div>
  );
}

export interface AssistantMessageProps {
  text: string;
  steps: ToolStep[];
  running: boolean;
  status?: string | null;
  errors?: string[];
  stopReason?: string | null;
  usage?: TurnUsage | null;
  /** Kronor per US dollar, from /v1/me; the cost is not shown until it is known. */
  sekPerUsd?: number | null;
}

export function AssistantMessage({ text: stored, steps, running, status, errors: live = [], stopReason, usage, sekPerUsd }: AssistantMessageProps) {
  const { text, errors: kept } = splitErrors(stored, stopReason === "error" || stopReason === "interrupted");
  const errors = [...kept, ...live];
  const note = stopNote(stopReason ?? null);
  const smooth = useSmoothText(text, running);
  const thinking = running && !text && !steps.length;

  return (
    <article className={styles.assistant} aria-busy={running}>
      <ActivitySteps steps={steps} running={running} />
      {thinking && (
        <p className={styles.thinking}>
          <Spinner /> {status ?? "Tänker …"}
        </p>
      )}
      {running && status && !thinking && !text && <p className={styles.thinking}>{status}</p>}
      {smooth && <Markdown streaming={running}>{smooth}</Markdown>}
      {errors.map((e) => (
        <Alert key={e} variant="critical" className={styles.alert}>
          {e}
        </Alert>
      ))}
      {!running && note && <p className={styles.note}>{note}</p>}
      {!running && text && (
        <div className={styles.actions}>
          <CopyButton text={text} />
          {usage && usage.costUsd > 0 && sekPerUsd && (
            <span
              className={styles.meta}
              title={`${usage.inputTokens + usage.cacheReadTokens + usage.cacheWriteTokens} tokens in · ${usage.outputTokens} ut`}
            >
              ≈ {formatSek(usage.costUsd, sekPerUsd)}
            </span>
          )}
        </div>
      )}
    </article>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <IconButton
      aria-label={copied ? "Kopierat" : "Kopiera svaret"}
      color="secondary"
      size="sm"
      icon={copied ? <CheckIcon /> : <ClipboardListIcon />}
      onClick={() =>
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
      }
    />
  );
}
